import React, { useEffect, useRef, useState } from 'react';
import i18next from 'i18next';
import { CloseIcon, ExclamationCircleIcon, Loader } from '@fluentui/react-northstar';
import { errorMessage, loadsAsImage } from './catalogImages';

/**
 * The catalog editor's picture button and pop-up, given to react-draft-wysiwyg as `image.component`. The library's
 * own pop-up keeps its state private, so it could not show progress in place of its text, could not clear a
 * picture once chosen, and dropped anything that was not a picture without a word. The props are the library's;
 * `onChange(src, height, width, alt)` inserts the picture and closes the pop-up. Class names are the library's
 * too, so Editor.css styles both.
 */
interface CatalogImagePopupProps {
  config: { icon: string; className?: string; popupClassName?: string; title?: string };
  expanded: boolean;
  onExpandEvent: () => void;
  doCollapse: () => void;
  onChange: (src: string, height: string, width: string, alt: string) => void;
  translations: Record<string, string>;
  /** Uploads the file and returns the address to show it from; throws an Error whose message is for the user. */
  upload?: (file: File) => Promise<string>;
  /** Hands back an uploaded image that was cleared, replaced, or never added. */
  discard?: (src: string) => void;
  /** Told when the pop-up opens and closes. */
  onOpenChange?: (open: boolean) => void;
  /** Gives the answer back the keyboard once a picture is in. */
  onAdded?: () => void;
}

/** What react-draft-wysiwyg hands the image pop-up it was given in place of its own. */
export type LibraryPopupProps = Pick<CatalogImagePopupProps, 'config' | 'expanded' | 'onExpandEvent' | 'doCollapse' | 'onChange' | 'translations'>;

export const CatalogImagePopup = ({ config, expanded, onExpandEvent, doCollapse, onChange, translations, upload, discard, onOpenChange, onAdded }: CatalogImagePopupProps) => {
  const [tab, setTab] = useState<'upload' | 'url'>(upload ? 'upload' : 'url');
  const [src, setSrc] = useState('');
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [checking, setChecking] = useState(false);
  const [linkError, setLinkError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const urlInput = useRef<HTMLInputElement>(null);
  // Bumped by every new file and by clearing, so an upload that finishes after either is ignored.
  const attempt = useRef(0);
  // The uploaded image on show, read when the pop-up closes - the state is stale inside that effect.
  const shown = useRef('');
  const added = useRef(false);

  const letGo = () => {
    if (shown.current && !added.current) discard?.(shown.current);
    shown.current = '';
  };

  // Switching the catalog item's tab removes the editor, pop-up and all, without closing it first.
  useEffect(() => () => letGo(), []);

  useEffect(() => onOpenChange?.(expanded), [expanded]);

  useEffect(() => {
    if (expanded) {
      added.current = false;
      return;
    }
    letGo();
    attempt.current++;
    setTab(upload ? 'upload' : 'url');
    setSrc('');
    setUrl('');
    setUploading(false);
    setError('');
    setDragOver(false);
    setChecking(false);
    setLinkError('');
  }, [expanded]);

  const take = async (file?: File) => {
    if (!file || !upload) return;
    const mine = ++attempt.current;
    letGo();
    setSrc('');
    setError('');
    setUploading(true);
    try {
      const link = await upload(file);
      if (attempt.current === mine) {
        shown.current = link;
        setSrc(link);
      } else {
        discard?.(link);
      }
    } catch (e: unknown) {
      if (attempt.current === mine) setError(errorMessage(e) || i18next.t('knowledge.add-knowledge-modal.image-upload-failed', { reason: '' }));
    } finally {
      if (attempt.current === mine) setUploading(false);
    }
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    letGo();
    attempt.current++;
    setSrc('');
    setError('');
  };

  const onDrop = (e: React.DragEvent) => {
    // Stopped here, or the editor underneath takes the same drop and inserts the picture a second time.
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (!uploading) take(e.dataTransfer?.files?.[0]);
  };

  // The tabs and buttons do not take focus, so it stayed in the answer: a Ctrl+V straight after opening the Link tab
  // pasted the address into the answer as text, behind the pop-up, above where the picture then went.
  useEffect(() => {
    if (expanded && tab === 'url') urlInput.current?.focus();
  }, [expanded, tab, linkError]);

  const chosen = tab === 'upload' ? src : url.trim();

  /** A link goes in only once it has loaded as a picture; anything else stays in the box with a reason. */
  const add = async () => {
    if (tab === 'upload') {
      added.current = true;
      onChange(chosen, 'auto', 'auto', '');
      onAdded?.();
      return;
    }
    const mine = ++attempt.current;
    const link = chosen;
    setLinkError('');
    setChecking(true);
    const ok = await loadsAsImage(link);
    if (attempt.current !== mine) return;
    setChecking(false);
    if (!ok) {
      setLinkError(i18next.t('knowledge.add-knowledge-modal.image-link-not-image'));
      return;
    }
    added.current = false;
    onChange(link, 'auto', 'auto', '');
    onAdded?.();
  };

  const renderUploadContent = () => {
    if (uploading)
      return (
        <div className="catalog-image-popup-progress" role="progressbar" aria-label={i18next.t('knowledge.add-knowledge-modal.image-uploading', { count: 1 })}>
          <span />
        </div>
      );
    if (src)
      return (
        <>
          <img src={src} alt="" className="rdw-image-modal-upload-option-image-preview" />
          <button type="button" className="catalog-image-popup-clear" onClick={clear}
            title={i18next.t('knowledge.add-knowledge-modal.image-remove')} aria-label={i18next.t('knowledge.add-knowledge-modal.image-remove')}>
            <CloseIcon size="small" />
          </button>
        </>
      );
    return (
      <span className={`rdw-image-modal-upload-option-label${error ? ' catalog-image-popup-label--error' : ''}`}>
        {error && (
          <span role="alert" className="catalog-image-popup-error">
            <ExclamationCircleIcon size="medium" className="catalog-editor-message-icon" />
            <span>{error}</span>
          </span>
        )}
        <span>{translations['components.controls.image.dropFileText']}</span>
      </span>
    );
  };

  const renderUploadArea = () => (
    <div
      className={`rdw-image-modal-upload-option${dragOver ? ' rdw-image-modal-upload-option-highlighted' : ''}${src ? ' catalog-image-popup-has-picture' : ''}`}
      onClick={() => { if (!uploading && !src) fileInput.current?.click(); }}
      onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (!dragOver) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      {renderUploadContent()}
      <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/gif" className="rdw-image-modal-upload-option-input"
        onChange={e => { take(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );

  return (
    <div className="rdw-image-wrapper" aria-haspopup="true" aria-expanded={expanded}>
      <div className={`rdw-option-wrapper ${config.className ?? ''}`} onClick={onExpandEvent}
        title={config.title || translations['components.controls.image.image']}>
        <img src={config.icon} alt="" />
      </div>
      {expanded && (
        // Clicks stop here, or they reach the editor, which closes every open pop-up.
        <div className={`rdw-image-modal ${config.popupClassName ?? ''}`} onClick={e => e.stopPropagation()}>
          <div className="rdw-image-modal-header">
            {upload && (
              <span onClick={() => setTab('upload')} className="rdw-image-modal-header-option">
                {translations['components.controls.image.fileUpload']}
                <span className={`rdw-image-modal-header-label${tab === 'upload' ? ' rdw-image-modal-header-label-highlighted' : ''}`} />
              </span>
            )}
            <span onClick={() => setTab('url')} className="rdw-image-modal-header-option">
              {translations['components.controls.image.byURL']}
              <span className={`rdw-image-modal-header-label${tab === 'url' ? ' rdw-image-modal-header-label-highlighted' : ''}`} />
            </span>
          </div>
          {tab === 'upload' ? renderUploadArea() : (
            <div className="rdw-image-modal-url-section">
              <input ref={urlInput} className="rdw-image-modal-url-input" placeholder={translations['components.controls.image.enterlink']}
                value={url} onChange={e => { setUrl(e.target.value); setLinkError(''); }} />
              {linkError && (
                <span role="alert" className="catalog-image-popup-error catalog-image-popup-link-error">
                  <ExclamationCircleIcon size="medium" className="catalog-editor-message-icon" />
                  <span>{linkError}</span>
                </span>
              )}
            </div>
          )}
          <span className="rdw-image-modal-btn-section">
            <button type="button" className="rdw-image-modal-btn" disabled={!chosen || uploading || checking} onClick={add}
              aria-busy={checking} aria-label={checking ? i18next.t('knowledge.add-knowledge-modal.image-link-checking') : undefined}>
              {checking ? <Loader size="smallest" inline /> : translations['generic.add']}
            </button>
            <button type="button" className="rdw-image-modal-btn" onClick={doCollapse}>
              {translations['generic.cancel']}
            </button>
          </span>
        </div>
      )}
    </div>
  );
};
